import {FC, useState, useEffect} from 'react'
import {Modal} from 'react-bootstrap'
import Swal from 'sweetalert2'
import { GET, POST } from '../../../../services/api'

type Booking = {
  id: number
  name: string
  people: number
  phone?: string
  email?: string
  bookingHourId: number
  date: string
  isAllocated?: boolean
  allocationId?: number
}

type Allocation = {
  id: number
  environmentTableId: number
  bookingId: number
  queueId?: number
  clientName: string
  people: number
  phone?: string
  email?: string
  status: string
}

type Table = {
  id: number
  tableNumber: string
  customCapacity?: number
  tableType?: {
    capacity: number
  }
  environmentId?: number
}

type Props = {
  show: boolean
  onHide: () => void
  table: Table | null
  allocation: Allocation | null
  availableBookings: Booking[]
  availableTables?: Table[]
  allocations?: Allocation[]
  filterHourId?: number | null  // ✅ ID da hora selecionada
  filterHour?: string  // ✅ Hora em formato "HH:MM"
  filterDate?: string  // ✅ Data selecionada em formato "YYYY-MM-DD"
  selectedEnvironmentId?: number | null  // ✅ Ambiente selecionado na página
  bookingHours?: any[]  // ✅ Array de horários disponíveis
  onAllocate: (tableId: number, bookingId: number) => Promise<void>
  onAllocateMultiple?: (tableIds: number[], bookingId: number) => Promise<void>
  onAllocateWalkIn?: (tableIds: number[], people: number, name: string, phone: string, email: string) => Promise<void>
  onDeallocate: (allocationId: number) => Promise<void>
  onChangeStatus?: (allocationId: number, newStatus: 'allocated' | 'occupied' | 'cleaning') => Promise<void>
  // Novos props para suporte a múltiplas mesas de uma reserva
  onFetchRelatedAllocations?: (
    bookingId: number,
    environmentId: number,
    date?: string,
    hour?: string,
    queueId?: number
  ) => Promise<Allocation[]>
  onChangeStatusBulk?: (allocationIds: number[], status: 'allocated' | 'occupied' | 'cleaning') => Promise<void>
  onDeallocateBulk?: (allocationIds: number[]) => Promise<void>
}

export const QuickAllocationModal: FC<Props> = ({
  show,
  onHide,
  table,
  allocation,
  availableBookings,
  availableTables = [],
  allocations = [],
  filterHourId,  // ✅ ID da hora
  filterHour,  // ✅ Hora string
  filterDate,  // ✅ Data selecionada
  selectedEnvironmentId,  // ✅ Ambiente selecionado
  bookingHours = [],  // ✅ Array de horários
  onAllocate,
  onAllocateMultiple,
  onAllocateWalkIn,
  onDeallocate,
  onChangeStatus,
  onFetchRelatedAllocations,
  onChangeStatusBulk,
  onDeallocateBulk,
}) => {
  const [loading, setLoading] = useState(false)
  const [shouldClose, setShouldClose] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null)
  const [showWalkInForm, setShowWalkInForm] = useState(false)
  const [walkInPeople, setWalkInPeople] = useState(2)
  const [walkInName, setWalkInName] = useState('')
  const [walkInPhone, setWalkInPhone] = useState('')
  const [walkInEmail, setWalkInEmail] = useState('')
  const [suggestedTables, setSuggestedTables] = useState<Table[]>([])
  const [multiBookingAllocation, setMultiBookingAllocation] = useState<Map<number, Table[]> | null>(null)
  const [tableCapacity, setTableCapacity] = useState(0)

  // Novos estados para suporte a múltiplas mesas de uma reserva
  const [relatedAllocations, setRelatedAllocations] = useState<Allocation[]>([])
  const [selectedAllocationIds, setSelectedAllocationIds] = useState<Set<number>>(new Set())
  const [showTabsUI, setShowTabsUI] = useState(false)
  const [activeTab, setActiveTab] = useState<'details' | 'relatedTables' | 'queue-positions'>('details')
  const [showMultiActionDialog, setShowMultiActionDialog] = useState(false)
  const [pendingAction, setPendingAction] = useState<{ type: 'occupied' | 'cleaning' | 'deallocate'; scope: 'single' | 'multi' } | null>(null)

  // 🆕 Novos estados para aba "Posições de Filas"
  const [queuePositions, setQueuePositions] = useState<any[]>([])
  const [selectedQueuePosition, setSelectedQueuePosition] = useState<any | null>(null)
  const [loadingQueuePositions, setLoadingQueuePositions] = useState(false)
  
  // 🆕 Estados para seleção de proposta de múltiplas mesas para filas
  const [queueProposals, setQueueProposals] = useState<any[]>([])  // Array de propostas do backend
  const [selectedQueueProposalIndex, setSelectedQueueProposalIndex] = useState<number | null>(null)  // Índice da proposta selecionada (não pré-seleciona)
  const [showQueueProposalSelection, setShowQueueProposalSelection] = useState(false)  // Exibir UI de seleção
  const [loadingQueueProposals, setLoadingQueueProposals] = useState(false)  // Loading propostas

  // Calcular automaticamente alocação otimizada para TODAS as reservas não alocadas
  useEffect(() => {
    if (!show || showWalkInForm || availableBookings.length < 2) {
      setMultiBookingAllocation(null)
      return
    }

    console.log('📊 Calculando alocação otimizada para todas as reservas...', availableBookings.length)
    const allocation = findOptimalAllocationForAllBookings(availableBookings, availableTables || [], allocations)
    
    if (allocation && Object.keys(allocation).length > 0) {
      const allocationMap = new Map<number, Table[]>()
      Object.entries(allocation).forEach(([bookingIdStr, tables]) => {
        allocationMap.set(Number(bookingIdStr), tables)
      })
      setMultiBookingAllocation(allocationMap)
      console.log('✅ Alocação multi-reserva calculada:', allocationMap)
    } else {
      setMultiBookingAllocation(null)
      console.log('⚠️ Não foi possível calcular alocação otimizada para todas as reservas')
    }
  }, [show, showWalkInForm, availableBookings, availableTables, allocations])

  // Calcular capacidade da mesa sempre que ela mudar
  useEffect(() => {
    const capacity = table?.customCapacity || table?.tableType?.capacity || 0
    setTableCapacity(capacity)
    console.log('📏 Capacidade da mesa atualizada:', capacity)
  }, [table])

  // Calcular automaticamente combinação quando reserva over-capacity é selecionada
  useEffect(() => {
    if (!selectedBookingId || !table || showWalkInForm) {
      setSuggestedTables([])
      return
    }

    const selectedBooking = availableBookings.find(b => b.id === selectedBookingId)
    if (!selectedBooking) {
      setSuggestedTables([])
      return
    }

    // Se a reserva cabe na mesa atual, não precisa de combinação
    if (selectedBooking.people <= tableCapacity) {
      setSuggestedTables([])
      return
    }

    // Se ultrapassou capacidade, calcular combinação automaticamente
    console.log('📋 Reserva selecionada ultrapassou capacidade, calculando combinação...')
    const combination = findBestTableCombination(selectedBooking.people, table, availableTables || [])
    setSuggestedTables(combination)
  }, [selectedBookingId, table, showWalkInForm, availableBookings, availableTables])

  // 🆕 Carregar posições de fila de hoje quando a aba é aberta
  useEffect(() => {
    if (activeTab !== 'queue-positions') {
      return
    }

    const loadQueuePositions = async () => {
      try {
        setLoadingQueuePositions(true)
        // ✅ Token é passado automaticamente pela lib no header Authorization
        // ✅ pluginspaceId é extraído do token no Backend
        const response = await GET('/queue-allocation/available-queue-positions-today')
        
        if (response?.success && Array.isArray(response.data)) {
          setQueuePositions(response.data)
          console.log('✅ Posições de fila carregadas:', response.data.length)
        } else {
          console.warn('⚠️ Resposta inválida da API:', response)
          setQueuePositions([])
        }
      } catch (error) {
        console.error('❌ Erro ao carregar posições de fila:', error)
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Erro ao carregar posições de fila. Tente novamente.',
        })
        setQueuePositions([])
      } finally {
        setLoadingQueuePositions(false)
      }
    }

    loadQueuePositions()
  }, [activeTab])

  // Buscar alocações relacionadas quando modal abre com alocação existente
  useEffect(() => {
    const fetchRelatedAllocations = async () => {
      // Apenas se estamos mostrando uma alocação existente (não no modo de alocação)
      if (!show || !allocation || !table || !onFetchRelatedAllocations) {
        setRelatedAllocations([])
        setSelectedAllocationIds(new Set())
        setShowTabsUI(false)
        setActiveTab('details')
        return
      }

      try {
        console.log('🔍 Buscando alocações relacionadas:', {
          bookingId: allocation.bookingId,
          queueId: allocation.queueId,
        })
        const related = await onFetchRelatedAllocations(
          allocation.bookingId,
          allocation.environmentTableId,
          undefined, // date
          undefined, // hour
          allocation.queueId, // Passar queueId para suportar alocações de fila
        )
        
        console.log('✅ Alocações relacionadas encontradas:', related)
        
        // Definir alocações relacionadas e mostrar abas se houver mais de 1
        setRelatedAllocations(related)
        
        // Se houver mais de 1 alocação para esta reserva, mostrar UI de tabs
        if (related.length > 1) {
          setShowTabsUI(true)
          // Pré-selecionar a alocação atual por padrão
          const selectedIds = new Set<number>()
          selectedIds.add(allocation.id)
          setSelectedAllocationIds(selectedIds)
          console.log('📊 UI de múltiplas mesas ativada. Alocações:', related.length)
        } else {
          setShowTabsUI(false)
          setSelectedAllocationIds(new Set())
        }
      } catch (error) {
        console.error('❌ Erro ao buscar alocações relacionadas:', error)
        setRelatedAllocations([])
        setSelectedAllocationIds(new Set())
        setShowTabsUI(false)
      }
    }

    fetchRelatedAllocations()
  }, [show, allocation, table, onFetchRelatedAllocations])

  // Inicializar seleção quando dialog de multi-ação abre (evita estado vazio)
  useEffect(() => {
    if (showMultiActionDialog && allocation && selectedAllocationIds.size === 0) {
      console.log('🔧 Pré-selecionando: apenas mesa atual (segurança padrão)')
      setSelectedAllocationIds(new Set([allocation.id]))
    }
  }, [showMultiActionDialog, allocation, selectedAllocationIds])

  // 🔒 Aguardar sincronização de dados antes de limpar quando modal fecha
  // Isso evita "stale data" quando ações completarem enquanto modal está fechando
  useEffect(() => {
    if (!show && (relatedAllocations.length > 0 || selectedAllocationIds.size > 0)) {
      // Modal está fechando com dados ainda presentes
      // Dar mais alguns ms para dados sincronizarem via props
      const syncGuardTimer = setTimeout(() => {
        console.log('🔒 Sincronização de dados completada, modal pode fechar com segurança')
      }, 100)
      
      return () => clearTimeout(syncGuardTimer)
    }
  }, [show, relatedAllocations.length, selectedAllocationIds.size])

  // Fechar modal quando ação foi concluída e loading voltou a false
  useEffect(() => {
    console.log('🔔 Modal close useEffect disparado:', { shouldClose, loading })
    if (shouldClose && !loading) {
      console.log('✅ Ação concluída, fechando modal...')
      // Limpar estados
      setSelectedBookingId(null)
      setShowWalkInForm(false)
      setWalkInPeople(2)
      setWalkInName('')
      setWalkInPhone('')
      setWalkInEmail('')
      setSuggestedTables([])
      setMultiBookingAllocation(null)
      setRelatedAllocations([])
      setSelectedAllocationIds(new Set())
      setShowTabsUI(false)
      setActiveTab('details')
      setShowMultiActionDialog(false)
      setPendingAction(null)
      // 🆕 Limpar estados de propostas de fila
      setQueueProposals([])
      setSelectedQueueProposalIndex(null)
      setShowQueueProposalSelection(false)
      setShouldClose(false)
      // Fechar modal
      console.log('📞 Chamando onHide()')
      onHide()
    }
  }, [shouldClose, loading, onHide])

  // Limpar form ao fechar modal
  const handleClose = () => {
    // 🔒 Guard: Impede fechamento se ação está em progresso
    if (loading || loadingQueueProposals) {
      console.log('⏳ Ação em progresso, aguardando sincronização de dados...')
      return
    }

    setSelectedBookingId(null)
    setShowWalkInForm(false)
    setWalkInPeople(2)
    setWalkInName('')
    setWalkInPhone('')
    setWalkInEmail('')
    setSuggestedTables([])
    setMultiBookingAllocation(null)
    setRelatedAllocations([])
    setSelectedAllocationIds(new Set())
    setShowTabsUI(false)
    setActiveTab('details')
    setShowMultiActionDialog(false)
    setPendingAction(null)
    // 🆕 Limpar estados de propostas de fila
    setQueueProposals([])
    setSelectedQueueProposalIndex(null)
    setShowQueueProposalSelection(false)
    onHide()
  }

  // 🆕 Função para alocar posição de fila - SEMPRE propõe múltiplas combinações
  const handleAllocateQueuePosition = async () => {

    if (!table || !selectedQueuePosition) {
      console.log('⚠️ Validação falhou:', { hasTable: !!table, hasQueuePosition: !!selectedQueuePosition })
      Swal.fire({
        icon: 'warning',
        title: 'Erro de Validação',
        text: 'Mesa ou posição de fila não selecionada. Por favor, tente novamente.',
      })
      return
    }

    console.log('🎯 Buscando propostas de alocação para posição de fila:', { 
      positionId: selectedQueuePosition.id,
      positionName: selectedQueuePosition.name,
      people: selectedQueuePosition.people,
      queueConfigId: selectedQueuePosition.queueConfigId
    })

    // 🆕 SEMPRE chamar endpoint de propostas (não verificar se cabe em 1 mesa)
    setLoadingQueueProposals(true)
    try {
      // ✅ Usar hora do filtro, COM fallback para hora atual se não houver seleção
      let hourToSend = filterHour
      let finalHourId = filterHourId
      
      if (!hourToSend) {
        // Fallback: usar hora atual
        const now = new Date()
        const hours = now.getHours().toString().padStart(2, '0')
        const minutes = now.getMinutes().toString().padStart(2, '0')
        hourToSend = `${hours}:${minutes}`
        
        console.warn('⚠️ filterHour não foi passado ao modal. Usando hora atual como fallback:', {
          filterHour,
          filterHourId,
          fallbackHour: hourToSend,
          now: new Date().toISOString(),
        })
      }
      
      if (hourToSend === '24:00') {
        console.log('⚠️ Hora 24:00 detectada na proposição - convertendo para 00:00')
        hourToSend = '00:00'
      }
      
      // ✅ NOVO: Obter data atual em formato YYYY-MM-DD para enviar ao backend
      // Se filterDate disponível, usa ele; senão calcula do dia atual
      let currentDateFormatted = filterDate
      if (!currentDateFormatted) {
        const today = new Date()
        currentDateFormatted = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${(today.getDate()).toString().padStart(2, '0')}`
      }
      // Se filterDate já está no formato YYYY-MM-DD, usar como está
      
      const payload = {
        bookingHourId: filterHourId || null,  // ✅ Usar horário do filtro se disponível
        environmentId: selectedEnvironmentId || null,  // ✅ Filtrar mesas do ambiente selecionado
        hour: hourToSend,  // ✅ Enviar hora string (já convertida de 24:00 se necessário)
        currentDate: currentDateFormatted,  // ✅ NOVO: Passa data atual
      }
      
      console.log('🔍 PAYLOAD ENVIADO PARA PROPOSIÇÃO:', {
        filterHourId,
        hourToSend,
        currentDateFormatted,
        payload,
        selectedQueuePosition: {
          id: selectedQueuePosition.id,
          people: selectedQueuePosition.people,
          queueConfigId: selectedQueuePosition.queueConfigId,
        }
      })
      
      const proposeResponse = await POST(
        `/queue-allocation/propose/${selectedQueuePosition.id}/${selectedQueuePosition.queueConfigId}`,
        payload
      )
      
      // ✅ Verificar se há erro real na requisição
      if (!proposeResponse?.success) {
        throw new Error('Erro na requisição ao buscar propostas')
      }

      // ⚠️ Se não há propostas (mas success=true), exibir AVISO com a razão específica
      if (!proposeResponse.data || proposeResponse.data.length === 0) {
        const warningMessage = proposeResponse?.message || 'Nenhuma proposta de alocação disponível para esta posição, confira as restrições de configuração'
        console.warn('⚠️ Nenhuma proposta encontrada:', warningMessage)
        
        // Exibir como AVISO, não erro
        Swal.fire({
          icon: 'warning',
          title: 'Nenhuma Proposta Disponível',
          text: warningMessage,
          confirmButtonText: 'Entendi',
        })
        return
      }

      console.log('✅ Propostas recebidas:', proposeResponse.data.length, 'opções')
      setQueueProposals(proposeResponse.data)

      // 🆕 Se apenas 1 proposta, auto-confirmar (UX perfeita para casos simples)
      if (proposeResponse.data.length === 1) {
        console.log('✅ Apenas 1 proposta disponível - confirmando automaticamente')
        const singleProposal = proposeResponse.data[0]
        await confirmQueueProposal(singleProposal, selectedQueuePosition)
      } else {
        setSelectedQueueProposalIndex(null)  // ✅ NÃO pré-selecionar - deixar usuário escolher
        setShowQueueProposalSelection(true)
      }
    } catch (error) {
      console.error('❌ Erro ao buscar propostas:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao Buscar Propostas',
        text: String(error) || 'Erro ao buscar propostas de alocação. Tente novamente.',
      })
    } finally {
      setLoadingQueueProposals(false)
    }
  }

  // 🆕 Função auxiliar para confirmar proposta de fila selecionada
  const confirmQueueProposal = async (proposal: any, queuePosition: any) => {
    if (!proposal || !queuePosition) {
      console.error('❌ Proposta ou posição inválida')
      return
    }

    setLoading(true)
    try {
      // 🔍 DEBUG: Log da data recebida
      console.log('[confirmQueueProposal] � ===== MODAL RECEBEU filterDate =====', {
        filterDate_recebido: filterDate,
        filterDate_type: typeof filterDate,
        filterDate_empty: !filterDate,
        filterDate_length: filterDate?.length,
        filterDate_chars_codes: filterDate ? Array.from(filterDate).map(c => `${c}:${c.charCodeAt(0)}`) : null,
        filterDate_parsed_date: filterDate ? new Date(filterDate) : null,
      })

      // ✅ Usar data filtrada selecionada pelo usuário OU data atual se não tiver filtrado
      let dateEnvironment = filterDate || ''
      if (!dateEnvironment) {
        // Se não houver filterDate, usar data atual
        const now = new Date()
        const year = now.getFullYear()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const day = String(now.getDate()).padStart(2, '0')
        dateEnvironment = `${year}-${month}-${day}`
        console.log('[confirmQueueProposal] 📅 Fallback para data atual:', dateEnvironment)
      }
      
      console.log('[confirmQueueProposal] 📅 DATA FINAL ANTES DE ENVIAR:', {
        dateEnvironment,
        dateEnvironment_type: typeof dateEnvironment,
      })
      
      // ✅ Obter hora: usar filterHour (sempre deve vir preenchido da página pai)
      let hourEnvironment = filterHour
      
      if (!hourEnvironment) {
        console.warn('⚠️ ERRO LÓGICO: hourEnvironment não foi passado ao modal! Isso nunca deveria acontecer.', { 
          filterHour, 
          dateEnvironment, 
          selectedEnvironmentId,
          filterHourId
        })
      }
      
      if (hourEnvironment === '24:00') {
        console.log('⚠️ Hora 24:00 detectada - convertendo para 00:00 do próximo dia')
        hourEnvironment = '00:00'
        
        // Incrementar a data para o próximo dia
        const dateParts = dateEnvironment.split('-')
        let year = parseInt(dateParts[0])
        let month = parseInt(dateParts[1])
        let day = parseInt(dateParts[2])
        
        day += 1
        const tomorrow = new Date(year, month - 1, day)
        year = tomorrow.getFullYear()
        month = tomorrow.getMonth() + 1
        day = tomorrow.getDate()
        
        dateEnvironment = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      }

      console.log('📋 Confirmando proposta de fila:', {
        queueId: queuePosition.id,
        tableIds: proposal.tableIds,
        environmentId: selectedEnvironmentId || proposal.environmentId || 1,
        dateEnvironment,
        hourEnvironment,
      })

      const payload = {
        selectedTableIds: proposal.tableIds,
        environmentId: selectedEnvironmentId || proposal.environmentId || 1,  // ✅ Usar ambiente da página
        dateEnvironment,  // ✅ NOVO: Data da alocação
        hourEnvironment,  // ✅ NOVO: Hora da alocação
      }

      console.log('[confirmQueueProposal] 📤 ===== PAYLOAD MONTADO PRONTO PARA ENVIAR =====', {
        selectedTableIds: payload.selectedTableIds,
        environmentId: payload.environmentId,
        dateEnvironment: payload.dateEnvironment,
        dateEnvironment_type: typeof payload.dateEnvironment,
        dateEnvironment_length: payload.dateEnvironment.length,
        dateEnvironment_chars_codigos: Array.from(payload.dateEnvironment).map(c => `${c}:${c.charCodeAt(0)}`),
        hourEnvironment: payload.hourEnvironment,
        hourEnvironment_type: typeof payload.hourEnvironment,
      })

      // 🔴 CRÍTICO: Log do JSON que será enviado exatamente como vai no body
      const payloadJSON = JSON.stringify(payload)
      console.log('[confirmQueueProposal] 🔴 JSON.stringify(payload) EXATAMENTE COMO SERÁ ENVIADO:', {
        json_string: payloadJSON,
        json_length: payloadJSON.length,
        json_parsed_back: JSON.parse(payloadJSON),
      })

      const confirmResponse = await POST(`/queue-allocation/confirm/${queuePosition.id}`, payload)
      
      // Log APÓS receber resposta
      console.log('[confirmQueueProposal] 📥 RESPOSTA DO BACKEND:', {
        success: confirmResponse?.success,
        data_id: confirmResponse?.data?.id,
        data_dateEnvironment: confirmResponse?.data?.dateEnvironment,
        data_allocatedAt: confirmResponse?.data?.allocatedAt,
      })

      if (confirmResponse?.success) {
        console.log('✅ Posição de fila alocada com sucesso')
        
        // Limpar estados
        setSelectedQueuePosition(null)
        setQueueProposals([])
        setSelectedQueueProposalIndex(null)
        setShowQueueProposalSelection(false)

        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          html: `
            Posição da fila de <strong>${queuePosition.name}</strong> (${queuePosition.people} pessoas) alocada com sucesso!<br>
            <small>Mesas: ${proposal.tableNumbers?.join(', ') || 'N/A'}</small>
          `,
        })
      } else {
        throw new Error(confirmResponse?.message || 'Erro ao confirmar alocação')
      }
    } catch (error) {
      console.error('❌ Erro ao confirmar proposta:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao Alocar',
        text: String(error) || 'Erro ao alocar a posição de fila. Tente novamente.',
      })
    } finally {
      setLoading(false)
      setShouldClose(true)
    }
  }

  const handleAllocate = async () => {
    console.log('🔘 Botão "Alocar Reserva Selecionada" clicado')
    console.log('📊 Estado atual:', { 
      hasTable: !!table, 
      hasSelectedBookingId: !!selectedBookingId,
      tableId: table?.id,
      selectedBookingId,
      tableCapacity,
      availableBookingsCount: availableBookings.length
    })
    
    if (!table || !selectedBookingId) {
      console.log('⚠️ Validação falhou:', { hasTable: !!table, hasSelectedBookingId: !!selectedBookingId })
      Swal.fire({
        icon: 'warning',
        title: 'Erro de Validação',
        text: 'Mesa ou reserva não selecionada. Por favor, tente novamente.',
      })
      return
    }

    // Obter dados da reserva selecionada
    const selectedBooking = availableBookings.find(b => b.id === selectedBookingId)
    if (!selectedBooking) {
      console.log('⚠️ Reserva selecionada não encontrada em availableBookings')
      console.log('📋 IDs disponíveis:', availableBookings.map(b => b.id))
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Reserva não encontrada. Por favor, selecione novamente.',
      })
      return
    }

    console.log('🎯 Tentando alocar reserva:', { 
      bookingName: selectedBooking.name,
      people: selectedBooking.people, 
      capacity: tableCapacity,
      fits: selectedBooking.people <= tableCapacity
    })

    // Se cabe na mesa atual, aloca direto
    if (selectedBooking.people <= tableCapacity) {
      console.log('✅ Reserva cabe em 1 mesa - alocando direto')
      setLoading(true)
      try {
        console.log('✅ Chamando onAllocate com:', { tableId: table.id, bookingId: selectedBookingId })
        if (!onAllocate) {
          console.error('❌ onAllocate não está definido')
          throw new Error('Callback onAllocate não está disponível')
        }
        await onAllocate(table.id, selectedBookingId)
        console.log('✅ onAllocate completado com sucesso')
        setSelectedBookingId(null)
      } catch (error) {
        console.error('❌ Erro ao alocar reserva:', error)
        Swal.fire({
          icon: 'error',
          title: 'Erro ao Alocar',
          text: String(error) || 'Erro ao alocar a reserva. Tente novamente.',
        })
      } finally {
        console.log('🔒 Finally: setando loading=false e shouldClose=true')
        setLoading(false)
        setShouldClose(true)  // Sinaliza para fechar modal após loading ficar false
      }
    } else {
      // Buscar combinação de mesas
      console.log('🔍 Reserva NÃO cabe em 1 mesa - buscando combinação de mesas...')
      console.log(`   Precisa de ${selectedBooking.people} pessoas, mesa tem ${tableCapacity}`)
      const combination = findBestTableCombination(selectedBooking.people, table, availableTables || [])
      console.log('📋 Combinação encontrada:', combination.length > 0 ? `${combination.length} mesas` : 'Nenhuma')
      
      if (combination.length > 0) {
        console.log('✅ Mostrando accordion com sugestão de mesas')
        setSuggestedTables(combination)
        // O accordion será mostrado automaticamente quando suggestedTables.length > 0
        // Exibir também um AlertBox avisando que precisa confirmar
        Swal.fire({
          icon: 'info',
          title: 'Múltiplas Mesas Necessárias',
          html: `
            A reserva de <strong>${selectedBooking.people} pessoas</strong> não cabe em uma única mesa.
            <br/><br/>
            Foram encontradas <strong>${combination.length} mesas</strong> que podem ser utilizadas juntas:
            <br/>
            <strong>${combination.map(t => t.tableNumber).join(' + ')}</strong>
            <br/><br/>
            Clique no botão <strong>"Confirmar Alocação"</strong> abaixo para prosseguir.
          `,
          confirmButtonText: 'Entendi',
        })
      } else {
        console.error('❌ Não foi possível encontrar combinação de mesas')
        Swal.fire({
          icon: 'warning',
          title: 'Acima da Capacidade do Ambiente',
          text: `Não há mesas disponíveis suficientes para acomodar ${selectedBooking.people} pessoas. A capacidade total disponível é insuficiente.`,
          confirmButtonText: 'Entendi',
        })
      }
    }
  }

  const handleDeallocate = async () => {
    if (!allocation) return

    setLoading(true)
    try {
      await onDeallocate(allocation.id)
    } catch (error) {
      console.error('Erro ao desalocar reserva:', error)
    } finally {
      setLoading(false)
      setShouldClose(true)  // Sinaliza para fechar modal após loading ficar false
    }
  }

  // Executar mudança de status em múltiplas ou única alocação
  const handleChangeStatusMulti = async (status: 'occupied' | 'cleaning') => {
    if (!allocation || !onChangeStatus && !onChangeStatusBulk) return

    // Se apenas 1 mesa ou seleção vazia, usar única
    if (selectedAllocationIds.size <= 1) {
      if (onChangeStatus) {
        await onChangeStatus(allocation.id, status)
        handleClose()
      }
      return
    }

    // Se múltiplas mesas selecionadas e tem bulk handler
    if (onChangeStatusBulk && selectedAllocationIds.size > 1) {
      setLoading(true)
      try {
        const allocationIds = Array.from(selectedAllocationIds)
        console.log(`🔄 Alterando status de ${allocationIds.length} mesas para '${status}'`)
        await onChangeStatusBulk(allocationIds, status)
      } catch (error) {
        console.error('❌ Erro ao alterar status em lote:', error)
        Swal.fire({
          icon: 'error',
          title: 'Erro ao alterar status',
          text: 'Tente novamente mais tarde',
        })
      } finally {
        setLoading(false)
        setShouldClose(true)  // Sinaliza para fechar modal após loading ficar false
      }
    }
  }

  // Executar deallocation em múltiplas ou única alocação
  const handleDeallocateMulti = async () => {
    if (!allocation) return

    // Se apenas 1 mesa selecionada ou sem seleção, usar única
    if (selectedAllocationIds.size <= 1) {
      setLoading(true)
      try {
        await onDeallocate(allocation.id)
      } catch (error) {
        console.error('❌ Erro ao desalocar reserva:', error)
      } finally {
        setLoading(false)
        setShouldClose(true)  // Sinaliza para fechar modal após loading ficar false
      }
      return
    }

    // Se múltiplas mesas selecionadas e tem bulk handler
    if (onDeallocateBulk && selectedAllocationIds.size > 1) {
      setLoading(true)
      try {
        const allocationIds = Array.from(selectedAllocationIds)
        console.log(`🗑️ Desalocando ${allocationIds.length} mesas`)
        await onDeallocateBulk(allocationIds)
      } catch (error) {
        console.error('❌ Erro ao desalocar mesas:', error)
        Swal.fire({
          icon: 'error',
          title: 'Erro ao desalocar mesas',
          text: 'Tente novamente mais tarde',
        })
      } finally {
        setLoading(false)
        setShouldClose(true)  // Sinaliza para fechar modal após loading ficar false
      }
    }
  }

  // Handler para alternar checkbox de alocação
  const toggleAllocationSelection = (allocationId: number) => {
    const newSelection = new Set(selectedAllocationIds)
    if (newSelection.has(allocationId)) {
      newSelection.delete(allocationId)
    } else {
      newSelection.add(allocationId)
    }
    setSelectedAllocationIds(newSelection)
  }

  // Handler para selecionar/desselecionar todas
  const toggleAllAllocations = (selectAll: boolean) => {
    if (selectAll) {
      const allIds = new Set(relatedAllocations.map(a => a.id))
      setSelectedAllocationIds(allIds)
    } else {
      setSelectedAllocationIds(new Set())
    }
  }

  const handleWalkInAllocate = async () => {
    if (!table || !onAllocateWalkIn || !walkInName.trim()) {
      console.log('⚠️ Validação falhou:', { table: !!table, onAllocateWalkIn: !!onAllocateWalkIn, walkInName })
      return
    }

    console.log('🎯 Tentando alocar walk-in:', { people: walkInPeople, capacity: tableCapacity })
    
    // Se cabe na mesa atual, aloca direto
    if (walkInPeople <= tableCapacity) {
      setLoading(true)
      try {
        console.log('✅ Alocando direto na mesa:', table.id)
        await onAllocateWalkIn([table.id], walkInPeople, walkInName, walkInPhone, walkInEmail)
      } catch (error) {
        console.error('❌ Erro ao alocar walk-in:', error)
      } finally {
        setLoading(false)
        setShouldClose(true)  // Sinaliza para fechar modal após loading ficar false
      }
    } else {
      // Sugerir união de mesas inline (sem modal separado)
      console.log('🔍 Buscando combinação de mesas...')
      const combination = findBestTableCombination(walkInPeople, table, availableTables || [])
      console.log('📋 Combinação encontrada:', combination)
      
      if (combination.length > 0) {
        setSuggestedTables(combination)
        // NÃO abre modal separado, apenas atualiza o estado
        // O accordion será mostrado automaticamente quando suggestedTables.length > 1
      } else {
        console.error('❌ Não foi possível encontrar combinação de mesas')
        Swal.fire({
          icon: 'warning',
          title: 'Mesas Insuficientes',
          text: `Não há mesas disponíveis suficientes para acomodar ${walkInPeople} pessoas.`,
          confirmButtonText: 'Entendi',
        })
      }
    }
  }

  const handleConfirmCombination = async () => {
    if (!onAllocateWalkIn || suggestedTables.length === 0) return
    
    setLoading(true)
    try {
      const tableIds = suggestedTables.map(t => t.id)
      console.log('✅ Confirmando união de mesas:', tableIds)
      await onAllocateWalkIn(tableIds, walkInPeople, walkInName, walkInPhone, walkInEmail)
    } catch (error) {
      console.error('❌ Erro ao confirmar união de mesas:', error)
    } finally {
      setLoading(false)
      setShouldClose(true)  // Sinaliza para fechar modal após loading ficar false
    }
  }

  const handleConfirmBookingCombination = async () => {
    if (!selectedBookingId || suggestedTables.length === 0 || !onAllocateMultiple) return
    
    setLoading(true)
    try {
      const tableIds = suggestedTables.map(t => t.id)
      console.log('✅ Confirmando alocação de reserva em múltiplas mesas:', tableIds)
      await onAllocateMultiple(tableIds, selectedBookingId)
      setSelectedBookingId(null)
      setSuggestedTables([])
    } catch (error) {
      console.error('❌ Erro ao confirmar alocação de reserva:', error)
    } finally {
      setLoading(false)
      setShouldClose(true)  // Sinaliza para fechar modal após loading ficar false
    }
  }

  // Recalcular sugestões quando mudar quantidade de pessoas
  const handlePeopleChange = (newPeople: number) => {
    setWalkInPeople(newPeople)
    
    // Se já tinha sugestão, recalcula
    if (suggestedTables.length > 0 && table) {
      console.log('🔄 Recalculando sugestão para', newPeople, 'pessoas')
      const newCombination = findBestTableCombination(newPeople, table, availableTables || [])
      setSuggestedTables(newCombination)
    }
  }

  // Função para formatar telefone com máscara (XX) XXXXX-XXXX
  const formatarTelefone = (valor: string): string => {
    // Remove tudo que não é número
    const apenasNumeros = valor.replace(/\D/g, '')
    
    // Limita a 12 dígitos (padrão brasileiro)
    const limitado = apenasNumeros.slice(0, 11)
    
    // Aplica máscara: (XX) XXXXX-XXXX
    if (limitado.length === 0) return ''
    if (limitado.length <= 2) return `(${limitado}`
    if (limitado.length <= 7) return `(${limitado.slice(0, 2)}) ${limitado.slice(2)}`
    return `(${limitado.slice(0, 2)}) ${limitado.slice(2, 7)}-${limitado.slice(7)}`
  }

  // Algoritmo para encontrar melhor alocação para MÚLTIPLAS reservas (Bin Packing)
  const findOptimalAllocationForAllBookings = (bookings: Booking[], tables: Table[], currentAllocations: Allocation[]): {[bookingId: number]: Table[]} | null => {
    console.log('🧮 Iniciando bin packing para múltiplas reservas...')
    console.log(`📊 Reservas: ${bookings.length}, Mesas: ${tables.length}, Alocações: ${currentAllocations.length}`)
    
    // Filtrar apenas mesas disponíveis
    const occupiedTableIds = new Set(currentAllocations.map(a => a.environmentTableId))
    const availableTables = tables.filter(t => !occupiedTableIds.has(t.id))
    
    console.log(`✅ Mesas disponíveis: ${availableTables.length}`)
    
    if (availableTables.length === 0) {
      console.log('❌ Nenhuma mesa disponível')
      return null
    }
    
    // Ordenar reservas por tamanho decrescente (First Fit Decreasing)
    const sortedBookings = [...bookings].sort((a, b) => b.people - a.people)
    console.log(`📋 Reservas ordenadas: ${sortedBookings.map(b => `${b.name}(${b.people})`).join(', ')}`)
    
    // Manter track de mesas usadas e suas alocações
    type TableAllocated = {
      table: Table
      usedCapacity: number
      bookings: number[]
    }
    
    const allocatedTables: TableAllocated[] = []
    const result: {[bookingId: number]: Table[]} = {}
    const failedBookings: Booking[] = []
    
    // Para cada reserva, encontrar melhor combinação de mesas
    for (const booking of sortedBookings) {
      console.log(`\n🔍 Processando ${booking.name} (${booking.people} pessoas)`)
      
      let assigned = false
      
      // Estratégia 1: Procurar mesa individual que caiba
      for (let i = 0; i < allocatedTables.length; i++) {
        const allocation = allocatedTables[i]
        const availableInThisTable = allocation.table.customCapacity || allocation.table.tableType?.capacity || 0
        const spaceLeft = availableInThisTable - allocation.usedCapacity
        
        if (spaceLeft >= booking.people) {
          console.log(`  ✓ Cabe na mesa ${allocation.table.tableNumber}: ${spaceLeft} espaços disponíveis`)
          allocation.usedCapacity += booking.people
          allocation.bookings.push(booking.id)
          result[booking.id] = [allocation.table]
          assigned = true
          break
        }
      }
      
      // Estratégia 2: Usar mesas não utilizadas ainda
      if (!assigned) {
        for (const table of availableTables) {
          const capacity = table.customCapacity || table.tableType?.capacity || 0
          
          // Verificar se já foi alocada
          if (!allocatedTables.find(a => a.table.id === table.id)) {
            if (capacity >= booking.people) {
              console.log(`  ✓ Usando nova mesa ${table.tableNumber} (${capacity} capacidade)`)
              allocatedTables.push({
                table,
                usedCapacity: booking.people,
                bookings: [booking.id]
              })
              result[booking.id] = [table]
              assigned = true
              break
            }
          }
        }
      }
      
      // Estratégia 3: Combinar múltiplas mesas
      if (!assigned) {
        console.log(`  🔗 Tentando combinar mesas...`)
        
        // Encontrar melhor combinação
        type Combo = {
          tables: Table[]
          totalCapacity: number
          waste: number
        }
        const combinations: Combo[] = []
        
        // Combinações de 2 mesas
        for (let i = 0; i < availableTables.length; i++) {
          for (let j = i + 1; j < availableTables.length; j++) {
            const t1 = availableTables[i]
            const t2 = availableTables[j]
            
            // Verificar se não foram alocadas
            const isT1Used = allocatedTables.find(a => a.table.id === t1.id)
            const isT2Used = allocatedTables.find(a => a.table.id === t2.id)
            
            if (!isT1Used && !isT2Used) {
              const cap1 = t1.customCapacity || t1.tableType?.capacity || 0
              const cap2 = t2.customCapacity || t2.tableType?.capacity || 0
              const totalCapacity = cap1 + cap2
              
              if (totalCapacity >= booking.people) {
                combinations.push({
                  tables: [t1, t2],
                  totalCapacity,
                  waste: totalCapacity - booking.people
                })
              }
            }
          }
        }
        
        // Combinações de 3 mesas
        for (let i = 0; i < availableTables.length; i++) {
          for (let j = i + 1; j < availableTables.length; j++) {
            for (let k = j + 1; k < availableTables.length; k++) {
              const t1 = availableTables[i]
              const t2 = availableTables[j]
              const t3 = availableTables[k]
              
              const isT1Used = allocatedTables.find(a => a.table.id === t1.id)
              const isT2Used = allocatedTables.find(a => a.table.id === t2.id)
              const isT3Used = allocatedTables.find(a => a.table.id === t3.id)
              
              if (!isT1Used && !isT2Used && !isT3Used) {
                const cap1 = t1.customCapacity || t1.tableType?.capacity || 0
                const cap2 = t2.customCapacity || t2.tableType?.capacity || 0
                const cap3 = t3.customCapacity || t3.tableType?.capacity || 0
                const totalCapacity = cap1 + cap2 + cap3
                
                if (totalCapacity >= booking.people) {
                  combinations.push({
                    tables: [t1, t2, t3],
                    totalCapacity,
                    waste: totalCapacity - booking.people
                  })
                }
              }
            }
          }
        }
        
        if (combinations.length > 0) {
          // Ordenar por: 1) menor desperdício, 2) menor quantidade de mesas
          combinations.sort((a, b) => {
            if (a.waste !== b.waste) return a.waste - b.waste
            return a.tables.length - b.tables.length
          })
          
          const best = combinations[0]
          console.log(`  ✓ Melhor combinação: ${best.tables.map(t => t.tableNumber).join('+')} (desperdício: ${best.waste})`)
          
          for (const table of best.tables) {
            allocatedTables.push({
              table,
              usedCapacity: best.tables.length === 1 ? booking.people : 0, // Distribuir capacidade
              bookings: [booking.id]
            })
          }
          result[booking.id] = best.tables
          assigned = true
        }
      }
      
      if (!assigned) {
        failedBookings.push(booking)
        console.log(`  ❌ Não foi possível alocar ${booking.name}`)
      }
    }
    
    console.log(`\n📊 Resultado final:`)
    console.log(`  ✅ Alocadas: ${Object.keys(result).length}/${bookings.length}`)
    console.log(`  ❌ Não alocadas: ${failedBookings.length}`)
    console.log(`  🪑 Mesas utilizadas: ${allocatedTables.length}`)
    
    return Object.keys(result).length > 0 ? result : null
  }

  // Algoritmo para encontrar melhor combinação de mesas
  const findBestTableCombination = (people: number, primaryTable: Table, tables: Table[]): Table[] => {
    console.log('🧮 Iniciando cálculo Best Fit para', people, 'pessoas')
    console.log('📋 Alocações existentes:', allocations.map(a => ({ tableId: a.environmentTableId, client: a.clientName })))
    
    // Filtrar apenas mesas DISPONÍVEIS (sem alocação)
    const occupiedTableIds = new Set(allocations.map(a => a.environmentTableId))
    console.log('🚫 Mesas ocupadas (IDs):', Array.from(occupiedTableIds))
    
    const availableTablesOnly = tables.filter(t => {
      const isOccupied = occupiedTableIds.has(t.id)
      const isDifferent = t.id !== primaryTable.id
      if (isOccupied) {
        console.log(`⛔ Mesa ${t.tableNumber} (ID: ${t.id}) está ocupada - IGNORADA`)
      }
      return isDifferent && !isOccupied
    })
    
    console.log('✅ Mesas disponíveis para união:', availableTablesOnly.map(t => `${t.tableNumber} (${t.customCapacity || t.tableType?.capacity || 0} lugares)`))
    
    const allTables = [primaryTable, ...availableTablesOnly].map(t => ({
      id: t.id,
      number: t.tableNumber,
      capacity: t.customCapacity || t.tableType?.capacity || 0,
      table: t
    }))
    
    console.log('🪑 Mesas disponíveis:', allTables.map(t => `${t.number} (${t.capacity})`).join(', '))
    
    type Combination = {
      tables: typeof allTables
      totalCapacity: number
      waste: number
      score: number
    }
    
    const combinations: Combination[] = []
    
    // Estratégia ajustada: prioriza menos desperdício quando significativo
    // Peso menor = favorece menos mesas, mas penaliza muito desperdício
    
    // 1. Tentar mesa única
    for (const t of allTables) {
      if (t.capacity >= people) {
        const waste = t.capacity - people
        // Penalizar desperdício maior que 50% da capacidade necessária
        const wastePenalty = waste > (people * 0.5) ? waste * 3 : waste
        const score = 100 + wastePenalty
        combinations.push({
          tables: [t],
          totalCapacity: t.capacity,
          waste,
          score
        })
        console.log(`  ✓ Mesa ${t.number} (${t.capacity}) - Desperdício: ${waste} - Penalidade: ${wastePenalty} - Score: ${score}`)
      }
    }
    
    // 2. Tentar combinações de 2 mesas
    for (let i = 0; i < allTables.length; i++) {
      for (let j = i + 1; j < allTables.length; j++) {
        const t1 = allTables[i]
        const t2 = allTables[j]
        const totalCapacity = t1.capacity + t2.capacity
        
        if (totalCapacity >= people) {
          const waste = totalCapacity - people
          const wastePenalty = waste > (people * 0.5) ? waste * 2 : waste
          const score = 150 + wastePenalty
          combinations.push({
            tables: [t1, t2],
            totalCapacity,
            waste,
            score
          })
          console.log(`  ✓ Mesas ${t1.number}+${t2.number} (${t1.capacity}+${t2.capacity}) - Desperdício: ${waste} - Penalidade: ${wastePenalty} - Score: ${score}`)
        }
      }
    }
    
    // 3. Tentar combinações de 3 mesas
    for (let i = 0; i < allTables.length; i++) {
      for (let j = i + 1; j < allTables.length; j++) {
        for (let k = j + 1; k < allTables.length; k++) {
          const t1 = allTables[i]
          const t2 = allTables[j]
          const t3 = allTables[k]
          const totalCapacity = t1.capacity + t2.capacity + t3.capacity
          
          if (totalCapacity >= people) {
            const waste = totalCapacity - people
            const wastePenalty = waste > (people * 0.5) ? waste * 2 : waste
            const score = 250 + wastePenalty
            combinations.push({
              tables: [t1, t2, t3],
              totalCapacity,
              waste,
              score
            })
            console.log(`  ✓ Mesas ${t1.number}+${t2.number}+${t3.number} (${t1.capacity}+${t2.capacity}+${t3.capacity}) - Desperdício: ${waste} - Penalidade: ${wastePenalty} - Score: ${score}`)
          }
        }
      }
    }
    
    // 4. Tentar combinações de 4 mesas (para grupos maiores)
    for (let i = 0; i < allTables.length; i++) {
      for (let j = i + 1; j < allTables.length; j++) {
        for (let k = j + 1; k < allTables.length; k++) {
          for (let m = k + 1; m < allTables.length; m++) {
            const t1 = allTables[i]
            const t2 = allTables[j]
            const t3 = allTables[k]
            const t4 = allTables[m]
            const totalCapacity = t1.capacity + t2.capacity + t3.capacity + t4.capacity
            
            if (totalCapacity >= people) {
              const waste = totalCapacity - people
              const wastePenalty = waste > (people * 0.5) ? waste * 1.5 : waste
              const score = 350 + wastePenalty
              combinations.push({
                tables: [t1, t2, t3, t4],
                totalCapacity,
                waste,
                score
              })
              console.log(`  ✓ Mesas ${t1.number}+${t2.number}+${t3.number}+${t4.number} (${totalCapacity}) - Desperdício: ${waste} - Score: ${score}`)
            }
          }
        }
      }
    }
    
    // 5. Tentar combinações de 5 mesas (para grupos muito grandes)
    if (allTables.length >= 5) {
      for (let i = 0; i < allTables.length; i++) {
        for (let j = i + 1; j < allTables.length; j++) {
          for (let k = j + 1; k < allTables.length; k++) {
            for (let m = k + 1; m < allTables.length; m++) {
              for (let n = m + 1; n < allTables.length; n++) {
                const t1 = allTables[i]
                const t2 = allTables[j]
                const t3 = allTables[k]
                const t4 = allTables[m]
                const t5 = allTables[n]
                const totalCapacity = t1.capacity + t2.capacity + t3.capacity + t4.capacity + t5.capacity
                
                if (totalCapacity >= people) {
                  const waste = totalCapacity - people
                  const wastePenalty = waste > (people * 0.5) ? waste * 1.5 : waste
                  const score = 450 + wastePenalty
                  combinations.push({
                    tables: [t1, t2, t3, t4, t5],
                    totalCapacity,
                    waste,
                    score
                  })
                  console.log(`  ✓ Mesas ${t1.number}+${t2.number}+${t3.number}+${t4.number}+${t5.number} (${totalCapacity}) - Desperdício: ${waste} - Score: ${score}`)
                }
              }
            }
          }
        }
      }
    }
    
    // Ordenar por score (menor é melhor = Best Fit)
    combinations.sort((a, b) => a.score - b.score)
    
    if (combinations.length === 0) {
      console.log('❌ Nenhuma combinação encontrada')
      console.log('💡 Verifique se as mesas possuem capacidade configurada (customCapacity ou tableType.capacity)')
      console.log('📊 Total de mesas disponíveis:', allTables.length)
      console.log('📊 Capacidade total disponível:', allTables.reduce((sum, t) => sum + t.capacity, 0))
      return []
    }
    
    const best = combinations[0]
    console.log(`⭐ MELHOR: ${best.tables.map(t => t.number).join('+')} (${best.totalCapacity} lugares) - Desperdício: ${best.waste} - Score: ${best.score}`)
    
    return best.tables.map(t => t.table)
  }

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {allocation ? (
            <>
              <i className="bi bi-gear-fill me-2 text-info"></i>
              Gerenciar Mesa {table?.tableNumber}
            </>
          ) : (
            <>
              <i className="bi bi-lightning-charge me-2 text-primary"></i>
              Alocação Rápida - Mesa {table?.tableNumber}
            </>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {allocation ? (
          // Modal de Desalocação
          <div>
            {/* ABAS: Detalhes e Mesas do Conjunto (apenas se múltiplas mesas) */}
            {showTabsUI && relatedAllocations.length > 1 && (
              <div className="mb-3">
                <ul className="nav nav-tabs" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                      id="details-tab"
                      type="button"
                      onClick={() => setActiveTab('details')}
                      role="tab"
                      aria-controls="details-content"
                      aria-selected={activeTab === 'details'}
                    >
                      <i className="bi bi-info-circle me-2"></i>
                      Detalhes
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'relatedTables' ? 'active' : ''}`}
                      id="related-tab"
                      type="button"
                      onClick={() => setActiveTab('relatedTables')}
                      role="tab"
                      aria-controls="related-content"
                      aria-selected={activeTab === 'relatedTables'}
                    >
                      <i className="bi bi-diagram-3 me-2"></i>
                      Mesas do Conjunto
                      <span className="badge bg-primary ms-2">{relatedAllocations.length}</span>
                    </button>
                  </li>
                </ul>
              </div>
            )}

            {/* Aba DETALHES - Informações da Reserva */}
            {activeTab === 'details' && (
              <div>
                <div className="alert alert-warning d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle-fill fs-2 me-3"></i>
                  <div>
                    <strong>Atenção!</strong> Ao desalocar esta mesa, a reserva retornará para a lista
                    de distribuição e poderá ser alocada em outra mesa.
                  </div>
                </div>

                <div className="card border-warning">
                  <div className="card-body">
                    <h6 className="card-title text-dark fw-bold mb-3">
                      <i className="bi bi-info-circle me-2"></i>
                      Informações da Reserva Atual
                    </h6>

                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label text-muted small">Cliente</label>
                        <p className="fw-bold mb-0">
                          <i className="bi bi-person-fill me-2 text-primary"></i>
                          {allocation.clientName}
                        </p>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label text-muted small">Pessoas</label>
                        <p className="fw-bold mb-0">
                          <i className="bi bi-people-fill me-2 text-success"></i>
                          {allocation.people} pessoa(s)
                        </p>
                      </div>

                      {allocation.phone && (
                        <div className="col-md-6">
                          <label className="form-label text-muted small">Telefone</label>
                          <p className="fw-bold mb-0">
                            <i className="bi bi-telephone-fill me-2 text-info"></i>
                            {allocation.phone}
                          </p>
                        </div>
                      )}

                      {allocation.email && (
                        <div className="col-md-6">
                          <label className="form-label text-muted small">E-mail</label>
                          <p className="fw-bold mb-0 text-break">
                            <i className="bi bi-envelope-fill me-2 text-secondary"></i>
                            {allocation.email}
                          </p>
                        </div>
                      )}
                      
                      <div className="col-12">
                        <label className="form-label text-muted small">Status Atual</label>
                        <p className="fw-bold mb-0">
                          <i className={`bi ${
                            allocation.status === 'occupied' ? 'bi-person-check-fill me-2' :
                            allocation.status === 'cleaning' ? 'bi-trash-fill me-2' :
                            'bi-check-circle-fill me-2'
                          }`} style={{color: 
                            allocation.status === 'occupied' ? '#fd397a' :
                            allocation.status === 'cleaning' ? '#009ef7' :
                            '#50cd89'
                          }}></i>
                          {allocation.status === 'occupied' ? 'Ocupada' :
                           allocation.status === 'cleaning' ? 'Em Limpeza' :
                           'Alocada'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Aba MESAS DO CONJUNTO - Seleção de Múltiplas Mesas */}
            {activeTab === 'relatedTables' && showTabsUI && relatedAllocations.length > 1 && (
              <div>
                <div className="alert alert-info d-flex align-items-center">
                  <i className="bi bi-info-circle-fill fs-2 me-3"></i>
                  <div>
                    <strong>Conjunto de Mesas da Reserva</strong>
                    <br />
                    <small>Esta reserva utiliza {relatedAllocations.length} mesas. Selecione quais aplicar a ação.</small>
                  </div>
                </div>

                <div className="card border-info mb-3">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        <i className="bi bi-diagram-3 me-2"></i>
                        Mesas Disponíveis
                      </h6>
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => toggleAllAllocations(true)}
                          title="Selecionar todas as mesas"
                        >
                          <i className="bi bi-check-all me-1"></i>
                          Todas
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => toggleAllAllocations(false)}
                          title="Deselecionar todas as mesas"
                        >
                          <i className="bi bi-x-lg me-1"></i>
                          Nenhuma
                        </button>
                      </div>
                    </div>

                    <div className="list-group">
                      {relatedAllocations.map((alloc) => {
                        const isSelected = selectedAllocationIds.has(alloc.id)
                        const isCurrent = alloc.id === allocation?.id
                        const table = availableTables?.find(t => t.id === alloc.environmentTableId)

                        return (
                          <div
                            key={alloc.id}
                            className={`list-group-item d-flex align-items-center gap-3 p-3 ${
                              isSelected ? 'bg-primary bg-opacity-10 border-primary' : ''
                            }`}
                            style={{cursor: 'pointer'}}
                            onClick={() => toggleAllocationSelection(alloc.id)}
                          >
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={isSelected}
                                  onChange={() => {}}
                                  style={{cursor: 'pointer'}}
                                />
                                <strong>Mesa {table?.tableNumber || `#${alloc.environmentTableId}`}</strong>
                                {isCurrent && (
                                  <span className="badge bg-success ms-2">
                                    <i className="bi bi-star-fill me-1"></i>
                                    Atual
                                  </span>
                                )}
                              </div>
                              <small className="text-muted ms-4">
                                <i className="bi bi-people-fill me-1"></i>
                                {alloc.people} pessoa(s) • Status:
                                <span
                                  className={`ms-1 badge ${
                                    alloc.status === 'occupied'
                                      ? 'bg-danger'
                                      : alloc.status === 'cleaning'
                                      ? 'bg-warning text-dark'
                                      : 'bg-success'
                                  }`}
                                >
                                  {alloc.status === 'occupied'
                                    ? 'Ocupada'
                                    : alloc.status === 'cleaning'
                                    ? 'Limpeza'
                                    : 'Alocada'}
                                </span>
                              </small>
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    <div className="alert alert-primary mt-3 mb-0 py-2 small">
                      <i className="bi bi-info-circle me-2"></i>
                      <strong>Seleção:</strong> {selectedAllocationIds.size} de {relatedAllocations.length}{' '}
                      mesa(s) selecionada(s)
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Modal de Alocação
          <div>
            <div className="alert alert-info d-flex align-items-center mb-3">
              <i className="bi bi-info-circle-fill fs-4 me-3"></i>
              <div>
                <strong>Mesa {table?.tableNumber}</strong> - Capacidade: {tableCapacity} pessoa(s)
                <br />
                <small className="text-muted">
                  {showWalkInForm
                    ? 'Preencha os dados do cliente walk-in'
                    : 'Selecione uma reserva abaixo para alocar nesta mesa'}
                </small>
              </div>
            </div>

            {/* Toggle entre Reserva, Walk-in e Filas - UX Igualitária com Hover Consistente */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
              {/* Botão: Com Reserva */}
              <button
                className={`btn transition-all ${!showWalkInForm && activeTab !== 'queue-positions' ? 'btn-primary shadow' : 'btn-outline-primary'}`}
                onClick={() => {
                  setShowWalkInForm(false)
                  setActiveTab('details')
                }}
                title="Alocar reserva pré-existente em mesas"
                style={{
                  transition: 'all 0.25s ease-in-out',
                  cursor: 'pointer',
                  padding: '0.65rem 1.2rem',
                  fontWeight: !showWalkInForm && activeTab !== 'queue-positions' ? '600' : '500',
                  letterSpacing: '0.5px',
                }}
                onMouseEnter={(e) => {
                  const isActive = !showWalkInForm && activeTab !== 'queue-positions'
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = isActive 
                    ? '0 6px 12px rgba(13, 110, 253, 0.35)' 
                    : '0 4px 8px rgba(13, 110, 253, 0.25)'
                }}
                onMouseLeave={(e) => {
                  const isActive = !showWalkInForm && activeTab !== 'queue-positions'
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = isActive 
                    ? '0 3px 6px rgba(13, 110, 253, 0.2)' 
                    : 'none'
                }}
              >
                <i className="bi bi-calendar-check me-2"></i>
                Com Reserva
              </button>

              {/* Botão: Walk-in */}
              <button
                className={`btn transition-all ${showWalkInForm ? 'btn-success shadow' : 'btn-outline-success'}`}
                onClick={() => setShowWalkInForm(true)}
                title="Alocar cliente sem reserva prévia (Walk-in)"
                style={{
                  transition: 'all 0.25s ease-in-out',
                  cursor: 'pointer',
                  padding: '0.65rem 1.2rem',
                  fontWeight: showWalkInForm ? '600' : '500',
                  letterSpacing: '0.5px',
                }}
                onMouseEnter={(e) => {
                  const isActive = showWalkInForm
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = isActive 
                    ? '0 6px 12px rgba(40, 167, 69, 0.35)' 
                    : '0 4px 8px rgba(40, 167, 69, 0.25)'
                }}
                onMouseLeave={(e) => {
                  const isActive = showWalkInForm
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = isActive 
                    ? '0 3px 6px rgba(40, 167, 69, 0.2)' 
                    : 'none'
                }}
              >
                <i className="bi bi-person-plus me-2"></i>
                Walk-in (Sem Reserva)
              </button>

              {/* Botão: Posições de Filas */}
              <button
                className={`btn transition-all ${activeTab === 'queue-positions' ? 'btn-info shadow' : 'btn-outline-info'}`}
                onClick={() => {
                  setShowWalkInForm(false)
                  setActiveTab('queue-positions')
                }}
                title="Alocar posição da fila para mesa"
                style={{
                  transition: 'all 0.25s ease-in-out',
                  cursor: 'pointer',
                  padding: '0.65rem 1.2rem',
                  fontWeight: activeTab === 'queue-positions' ? '600' : '500',
                  letterSpacing: '0.5px',
                }}
                onMouseEnter={(e) => {
                  const isActive = activeTab === 'queue-positions'
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = isActive 
                    ? '0 6px 12px rgba(23, 162, 184, 0.35)' 
                    : '0 4px 8px rgba(23, 162, 184, 0.25)'
                }}
                onMouseLeave={(e) => {
                  const isActive = activeTab === 'queue-positions'
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = isActive 
                    ? '0 3px 6px rgba(23, 162, 184, 0.2)' 
                    : 'none'
                }}
              >
                <i className="bi bi-list-ul me-2"></i>
                Posições de Filas
              </button>
            </div>

            {/* Formulário Walk-in */}
            {showWalkInForm && (
              <div className="card border-success mb-3">
                <div className="card-body">
                  <h6 className="card-title text-success mb-3">
                    <i className="bi bi-person-plus-fill me-2"></i>
                    Alocação Walk-in
                  </h6>

                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Nome do Cliente <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={walkInName}
                      onChange={(e) => setWalkInName(e.target.value)}
                      placeholder="Digite o nome do cliente..."
                      disabled={loading}
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        Telefone <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        value={walkInPhone}
                        onChange={(e) => setWalkInPhone(formatarTelefone(e.target.value))}
                        placeholder="(00) 00000-0000"
                        disabled={loading}
                        maxLength={15}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">E-mail</label>
                      <input
                        type="email"
                        className="form-control"
                        value={walkInEmail}
                        onChange={(e) => setWalkInEmail(e.target.value)}
                        placeholder="email@exemplo.com"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Número de Pessoas <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => handlePeopleChange(Math.max(1, walkInPeople - 1))}
                        disabled={loading || walkInPeople <= 1}
                      >
                        <i className="bi bi-dash"></i>
                      </button>
                      <input
                        type="number"
                        className="form-control text-center"
                        min={1}
                        max={20}
                        value={walkInPeople}
                        onChange={(e) => {
                          const val = Number(e.target.value)
                          if (val >= 1 && val <= 20) handlePeopleChange(val)
                        }}
                        disabled={loading}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => handlePeopleChange(Math.min(20, walkInPeople + 1))}
                        disabled={loading || walkInPeople >= 20}
                      >
                        <i className="bi bi-plus"></i>
                      </button>
                    </div>
                    <small className="text-muted">Capacidade da mesa: {tableCapacity}</small>
                  </div>

                  {walkInPeople > tableCapacity && (
                    <div className="alert alert-warning mb-3">
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      <strong>Atenção:</strong> O número de pessoas ({walkInPeople}) excede a capacidade da mesa ({tableCapacity}).
                      Será necessário unir mesas.
                    </div>
                  )}

                  {/* Accordion de União de Mesas - Mostra sempre que houver sugestão */}
                  {suggestedTables.length > 0 && (
                    <div className="accordion mb-3" id="accordionCombination">
                      <div className="accordion-item border-warning">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button bg-warning bg-opacity-10"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseCombination"
                            aria-expanded="true"
                          >
                            <i className="bi bi-diagram-3 me-2 text-warning"></i>
                            <strong>União de Mesas Necessária</strong>
                          </button>
                        </h2>
                        <div
                          id="collapseCombination"
                          className="accordion-collapse collapse show"
                          data-bs-parent="#accordionCombination"
                        >
                          <div className="accordion-body p-2">
                            {/* Resumo da combinação */}
                            <div className="bg-primary bg-opacity-10 border border-primary rounded p-2 mb-2 d-flex align-items-center">
                              <i className="bi bi-diagram-3-fill text-primary fs-6 me-2"></i>
                              <div className="flex-grow-1">
                                <strong className="small d-block mb-0 text-primary">Melhor Combinação:</strong>
                                <div className="text-dark small lh-1">
                                  {suggestedTables.map((t, index) => {
                                    const capacity = t.customCapacity || t.tableType?.capacity || 0
                                    return (
                                      <span key={t.id}>
                                        {index > 0 && (
                                          <span className="text-muted mx-1">
                                            <i className="bi bi-plus-lg"></i>
                                          </span>
                                        )}
                                        <strong>Mesa {t.tableNumber}</strong>
                                        <span className="text-muted"> ({capacity} {capacity === 1 ? 'lugar' : 'lugares'})</span>
                                      </span>
                                    )
                                  })}
                                  {' '}
                                  <span className="text-success fw-bold">
                                    = {suggestedTables.reduce((sum, t) => sum + (t.customCapacity || t.tableType?.capacity || 0), 0)} lugares
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Lista de mesas */}
                            <div className="list-group list-group-flush mb-2">
                              {suggestedTables.map((t, index) => {
                                const capacity = t.customCapacity || t.tableType?.capacity || 0
                                return (
                                  <div
                                    key={t.id}
                                    className="list-group-item d-flex justify-content-between align-items-center py-2 px-2"
                                  >
                                    <div className="d-flex align-items-center">
                                      {index === 0 && (
                                        <span className="badge bg-primary me-2 small">Principal</span>
                                      )}
                                      <div>
                                        <strong className="d-block small">Mesa {t.tableNumber}</strong>
                                        <small className="text-muted">
                                          <i className="bi bi-people me-1"></i>
                                          {capacity} pessoa(s)
                                        </small>
                                      </div>
                                    </div>
                                    <i className="bi bi-check-circle-fill text-success"></i>
                                  </div>
                                )
                              })}
                            </div>

                            <div className="alert alert-info mb-0 py-2 small">
                              <i className="bi bi-info-circle me-2"></i>
                              As mesas ficarão <strong>vinculadas</strong> até a desalocação.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    className="btn btn-success w-100"
                    onClick={suggestedTables.length > 0 ? handleConfirmCombination : handleWalkInAllocate}
                    disabled={!walkInName.trim() || !walkInPhone.trim() || loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Alocando...
                      </>
                    ) : suggestedTables.length > 0 ? (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        {suggestedTables.length > 1 ? 'Confirmar União de Mesas' : 'Confirmar Alocação'}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Alocar Walk-in
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Lista de Reservas */}
            {!showWalkInForm && activeTab !== 'queue-positions' && (
              <>
                {/* Sugestão de Alocação Otimizada para Múltiplas Reservas */}
                {multiBookingAllocation && availableBookings.length > 1 && (
                  <div className="alert alert-success mb-3 border-success" style={{borderWidth: '2px'}}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="alert-heading mb-2">
                          <i className="bi bi-lightbulb-fill me-2" style={{color: '#198754'}}></i>
                          <strong>Proposta Otimizada para Todas as Reservas</strong>
                        </h6>
                        <small className="text-secondary">
                          O sistema calculou a melhor alocação para todas as {availableBookings.length} reservas não alocadas, minimizando o desperdício de mesas.
                        </small>
                      </div>
                    </div>
                    
                    <div className="mt-2 pt-2 border-top">
                      {multiBookingAllocation && Array.from(multiBookingAllocation.entries()).map(([bookingId, tables]) => {
                        const booking = availableBookings.find(b => b.id === bookingId)
                        if (!booking) return null
                        
                        return (
                          <div key={bookingId} className="mb-2">
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="flex-grow-1">
                                <strong className="small">{booking.name}</strong>
                                <span className="text-muted small ms-2">({booking.people} pessoas)</span>
                              </div>
                              <div className="small text-secondary">
                                {tables.map((t, idx) => (
                                  <span key={t.id}>
                                    {idx > 0 && <span className="mx-1">+</span>}
                                    <span className="badge bg-secondary">{t.tableNumber}</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    
                    <button
                      className="btn btn-success btn-sm mt-2 w-100"
                      onClick={async () => {
                        setLoading(true)
                        try {
                          for (const [bookingId, tables] of multiBookingAllocation.entries()) {
                            const tableIds = tables.map(t => t.id)
                            if (onAllocateMultiple) {
                              console.log(`✅ Alocando ${bookingId} em ${tableIds.join('+')}`)
                              await onAllocateMultiple(tableIds, bookingId)
                            }
                          }
                          Swal.fire({
                            icon: 'success',
                            title: 'Sucesso!',
                            text: 'Todas as reservas foram alocadas otimamente!',
                            confirmButtonText: 'Ok'
                          })
                          handleClose()
                        } catch (error) {
                          console.error('Erro ao alocar múltiplas reservas:', error)
                          Swal.fire({
                            icon: 'error',
                            title: 'Erro',
                            text: 'Erro ao alocar as reservas. Tente novamente.',
                            confirmButtonText: 'Ok'
                          })
                        } finally {
                          setLoading(false)
                        }
                      }}
                      disabled={loading}
                    >
                      <i className="bi bi-check-circle me-2"></i>
                      Alocar Todas as Reservas
                    </button>
                  </div>
                )}
                
                {availableBookings.length === 0 ? (
                  <div className="alert alert-warning">
                    <i className="bi bi-exclamation-circle me-2"></i>
                    Não há reservas disponíveis para alocação no filtro atual.
                  </div>
                ) : (
                  <>
                    <div className="list-group" style={{maxHeight: '400px', overflowY: 'auto'}}>
                      {availableBookings.map((booking) => {
                        const isOverCapacity = booking.people > tableCapacity
                        const isSelected = selectedBookingId === booking.id

                        return (
                          <button
                            key={booking.id}
                            type="button"
                            className={`list-group-item list-group-item-action ${
                              isSelected ? 'active' : ''
                            } ${isOverCapacity ? 'border-warning' : ''}`}
                            onClick={() => {
                              setSelectedBookingId(booking.id)
                              setSuggestedTables([]) // Limpar sugestões ao selecionar nova reserva
                              
                              // Avisar se houver alocação multi-reserva proposta
                              if (multiBookingAllocation && multiBookingAllocation.size > 1) {
                                console.log('⚠️ AVISO: Tem alocação otimizada disponível para TODAS as reservas')
                              }
                            }}
                            disabled={loading}
                          >
                            <div className="d-flex w-100 justify-content-between align-items-center">
                              <div>
                                <h6 className={`mb-1 ${isSelected ? 'text-white' : 'text-dark'}`}>
                                  <i className="bi bi-person-circle me-2"></i>
                                  {booking.name}
                                </h6>
                                <p className={`mb-1 small ${isSelected ? 'text-white-50' : 'text-muted'}`}>
                                  <i className="bi bi-people me-1"></i>
                                  {booking.people} pessoa(s)
                                  {booking.phone && (
                                    <>
                                      {' | '}
                                      <i className="bi bi-telephone me-1"></i>
                                      {booking.phone}
                                    </>
                                  )}
                                </p>
                              </div>
                              {isOverCapacity && (
                            <span className="badge bg-warning text-dark">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Acima da capacidade
                            </span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                    </div>

                    {/* Botão: Alocar a reserva selecionada individualmente */}
                    {/* {selectedBookingId && !showWalkInForm && (
                      <button
                        type="button"
                        className="btn btn-primary w-100 mb-3"
                        onClick={handleAllocate}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Alocando...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-2"></i>
                            Alocar Reserva Selecionada
                          </>
                        )}
                      </button>
                    )} */}

                    {/* Accordion de União de Mesas para Reservas */}
                    {suggestedTables.length > 0 && !showWalkInForm && (
                      <>
                        {/* Aviso: Alocar individualmente pode fragmentar mesas */}
                        {multiBookingAllocation && multiBookingAllocation.size > 1 && (
                          <div className="alert alert-info mb-3 d-flex align-items-start" role="alert">
                            <i className="bi bi-lightbulb me-3 mt-1 flex-shrink-0" style={{fontSize: '1.2em', color: '#0d6efd'}}></i>
                            <div className="w-100">
                              <h6 className="alert-heading mb-2">💡 Sugestão para Resultado Ótimo</h6>
                              <p className="mb-2 small">
                                Você está alocando <strong>uma a uma</strong>. Para melhor aproveitamento das mesas e <strong>garantir que todas as {availableBookings.length} reservas caibam</strong>, 
                                recomendamos usar <strong>"Alocar Todas as Reservas"</strong> que aparece acima. Isso evita fragmentação de mesas.
                              </p>
                              <small className="text-secondary">Cada alocação individual consome mesas que poderiam ser compartilhadas de forma mais eficiente.</small>
                            </div>
                          </div>
                        )}

                        {/* Mapa Visual: Roteiro de Alocação Ideal */}
                        {multiBookingAllocation && multiBookingAllocation.size > 1 && (
                          <div className="card border-success mb-3" style={{backgroundColor: '#f0fdf4'}}>
                            <div className="card-body p-3">
                              <h6 className="card-title text-success mb-3">
                                <i className="bi bi-diagram-3 me-2"></i>
                                <strong>📋 Roteiro de Alocação Ideal</strong>
                              </h6>
                              
                              <div className="d-flex flex-column gap-2">
                                {multiBookingAllocation && Array.from(multiBookingAllocation.entries()).map(([bookingId, tables], index) => {
                                  const booking = availableBookings.find(b => b.id === bookingId)
                                  if (!booking) return null
                                  
                                  const totalCapacity = tables.reduce((sum, t) => sum + (t.customCapacity || t.tableType?.capacity || 0), 0)
                                  const waste = totalCapacity - booking.people
                                  
                                  return (
                                    <div key={bookingId} className="p-2 bg-white rounded border border-success border-opacity-25">
                                      <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div className="flex-grow-1">
                                          <strong className="text-dark d-block">
                                            <span className="badge bg-success me-2">{index + 1}</span>
                                            {booking.name}
                                          </strong>
                                          <small className="text-muted">
                                            <i className="bi bi-people-fill me-1"></i>
                                            {booking.people} pessoa(s)
                                          </small>
                                        </div>
                                      </div>
                                      
                                      <div className="d-flex align-items-center gap-2 flex-wrap">
                                        {tables.map((t, tIdx) => {
                                          const capacity = t.customCapacity || t.tableType?.capacity || 0
                                          return (
                                            <div key={t.id} className="d-flex align-items-center">
                                              <span className="badge bg-light text-dark border border-success">
                                                <i className="bi bi-square-fill me-1" style={{color: '#22c55e'}}></i>
                                                Mesa {t.tableNumber}
                                              </span>
                                              {tIdx < tables.length - 1 && (
                                                <span className="mx-2 text-success fw-bold">
                                                  <i className="bi bi-plus-lg"></i>
                                                </span>
                                              )}
                                            </div>
                                          )
                                        })}
                                        <span className="badge bg-success bg-opacity-20 text-success ms-2">
                                          <i className="bi bi-arrow-right-short"></i>
                                          {totalCapacity} lugares
                                          {waste > 0 && <span className="ms-1">({waste} vago)</span>}
                                        </span>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                              
                              <div className="alert alert-success mb-0 mt-3 py-2 small">
                                <i className="bi bi-check-circle-fill me-2"></i>
                                <strong>Total: {multiBookingAllocation && Array.from(multiBookingAllocation.entries()).reduce((sum, [bid, tbls]) => {
                                  const booking = availableBookings.find(b => b.id === bid)
                                  return sum + (booking?.people || 0)
                                }, 0)} pessoas</strong> 
                                {' em '} 
                                <strong>{multiBookingAllocation ? new Set(Array.from(multiBookingAllocation.values()).flat().map(t => t.id)).size : 0} mesas</strong>
                                {' com mínimo desperdício'}
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="accordion mt-3 mb-3" id="accordionBookingCombination">
                        <div className="accordion-item border-warning">
                          <h2 className="accordion-header">
                            <button
                              className="accordion-button bg-warning bg-opacity-10"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseBookingCombination"
                              aria-expanded="true"
                            >
                              <i className="bi bi-diagram-3 me-2 text-warning"></i>
                              <strong>Mesas Sugeridas para a Reserva</strong>
                            </button>
                          </h2>
                          <div
                            id="collapseBookingCombination"
                            className="accordion-collapse collapse show"
                            data-bs-parent="#accordionBookingCombination"
                          >
                            <div className="accordion-body p-2">
                              {/* Resumo da combinação */}
                              <div className="bg-primary bg-opacity-10 border border-primary rounded p-2 mb-2 d-flex align-items-center">
                                <i className="bi bi-diagram-3-fill text-primary fs-6 me-2"></i>
                                <div className="flex-grow-1">
                                  <strong className="small d-block mb-0 text-primary">Combinação Recomendada:</strong>
                                  <div className="text-dark small lh-1">
                                    {suggestedTables.map((t, index) => {
                                      const capacity = t.customCapacity || t.tableType?.capacity || 0
                                      return (
                                        <span key={t.id}>
                                          {index > 0 && (
                                            <span className="text-muted mx-1">
                                              <i className="bi bi-plus-lg"></i>
                                            </span>
                                          )}
                                          <strong>Mesa {t.tableNumber}</strong>
                                          <span className="text-muted"> ({capacity} {capacity === 1 ? 'lugar' : 'lugares'})</span>
                                        </span>
                                      )
                                    })}
                                    {' '}
                                    <span className="text-success fw-bold">
                                      = {suggestedTables.reduce((sum, t) => sum + (t.customCapacity || t.tableType?.capacity || 0), 0)} lugares
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Lista de mesas sugeridas */}
                              <div className="list-group list-group-flush mb-2">
                                {suggestedTables.map((t, index) => {
                                  const capacity = t.customCapacity || t.tableType?.capacity || 0
                                  return (
                                    <div
                                      key={t.id}
                                      className="list-group-item d-flex justify-content-between align-items-center py-2 px-2"
                                    >
                                      <div className="d-flex align-items-center">
                                        {index === 0 && (
                                          <span className="badge bg-primary me-2 small">Principal</span>
                                        )}
                                        <div>
                                          <strong className="d-block small">Mesa {t.tableNumber}</strong>
                                          <small className="text-muted">
                                            <i className="bi bi-people me-1"></i>
                                            {capacity} pessoa(s)
                                          </small>
                                        </div>
                                      </div>
                                      <i className="bi bi-check-circle-fill text-success"></i>
                                    </div>
                                  )
                                })}
                              </div>

                              <div className="alert alert-info mb-0 py-2 small">
                                <i className="bi bi-info-circle me-2"></i>
                                As mesas ficarão <strong>vinculadas</strong> até a desalocação.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Botão para confirmar alocação em múltiplas mesas (reserva) */}
                      {selectedBookingId && (
                        <button
                          className="btn btn-success w-100 mt-3"
                          onClick={handleConfirmBookingCombination}
                          disabled={loading || !onAllocateMultiple}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Alocando em múltiplas mesas...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-check-circle me-2"></i>
                              Confirmar Alocação em {suggestedTables.length} Mesa{suggestedTables.length > 1 ? 's' : ''}
                            </>
                          )}
                        </button>
                      )}
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {/* Aba de Posições de Filas */}
            {activeTab === 'queue-positions' && (
              <div className="card border-warning">
                <div className="card-header bg-warning bg-opacity-10 border-bottom border-warning">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-list-ul me-2 text-info fs-5"></i>
                    <h6 className="mb-0 text-info">Posições de Filas de Hoje</h6>
                    {loadingQueuePositions && (
                      <span className="spinner-border spinner-border-sm ms-2" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="card-body">
                  {loadingQueuePositions ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-warning" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <p className="text-muted mt-2">Carregando posições da fila...</p>
                    </div>
                  ) : queuePositions.length === 0 ? (
                    <div className="alert alert-info mb-0">
                      <i className="bi bi-info-circle me-2"></i>
                      Nenhuma posição de fila disponível para alocação hoje.
                    </div>
                  ) : (
                    <>
                      <div className="list-group list-group-flush">
                        {queuePositions.map((position) => {
                          const isSelected = selectedQueuePosition?.id === position.id
                          const fitsInTable = position.people <= tableCapacity
                          const createdTime = new Date(position.createdAt).toLocaleTimeString('pt-BR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })
                          
                          return (
                            <div
                              key={position.id}
                              className={`list-group-item cursor-pointer transition-all ${
                                isSelected ? 'bg-warning bg-opacity-25 border-warning border-2' : 'border-1'
                              }`}
                              onClick={() => setSelectedQueuePosition(isSelected ? null : position)}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="d-flex align-items-start">
                                <div className="form-check me-3 pt-1">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    checked={isSelected}
                                    onChange={() => {}}
                                    style={{ cursor: 'pointer' }}
                                  />
                                </div>
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <strong className="text-dark">{position.name}</strong>
                                    {position.preferential && (
                                      <span className="badge bg-info">
                                        <i className="bi bi-star-fill me-1"></i>
                                        Preferencial
                                      </span>
                                    )}
                                    {!fitsInTable && (
                                      <span className="badge bg-danger">
                                        <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                        Acima da capacidade
                                      </span>
                                    )}
                                  </div>
                                  <small className="text-muted d-flex align-items-center gap-3 flex-wrap">
                                    <span>
                                      <i className="bi bi-people-fill me-1"></i>
                                      {position.people} pessoa{position.people !== 1 ? 's' : ''}
                                    </span>
                                    <span>
                                      <i className="bi bi-telephone me-1"></i>
                                      {position.phone || 'Sem telefone'}
                                    </span>
                                    <span>
                                      <i className="bi bi-clock me-1"></i>
                                      {createdTime}
                                    </span>
                                  </small>
                                  {position.email && (
                                    <small className="text-muted d-block mt-1">
                                      <i className="bi bi-envelope me-1"></i>
                                      {position.email}
                                    </small>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>

                      {selectedQueuePosition && !showQueueProposalSelection && (
                        <div className="mt-3">
                          <div className="alert alert-warning mb-2">
                            <i className="bi bi-info-circle me-2"></i>
                            <strong>Selecionado:</strong> {selectedQueuePosition.name} ({selectedQueuePosition.people} pessoa{selectedQueuePosition.people !== 1 ? 's' : ''})
                          </div>

                          <button
                            className="btn btn-success w-100"
                            onClick={handleAllocateQueuePosition}
                            disabled={loadingQueueProposals}
                          >
                            {loadingQueueProposals ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Buscando propostas...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-check-circle me-2"></i>
                                Alocar Posição de Fila na Mesa {table?.tableNumber}
                              </>
                            )}
                          </button>
                        </div>
                      )}

                      {/* 🆕 UI de Seleção de Proposta para Múltiplas Mesas */}
                      {showQueueProposalSelection && queueProposals.length > 0 && selectedQueuePosition && (
                        <div className="mt-3">
                          <div className="card border-success mb-3">
                            <div className="card-header bg-success bg-opacity-10 border-bottom border-success">
                              <h6 className="mb-0 text-success">
                                <i className="bi bi-list-check me-2"></i>
                                Opções de Alocação Disponíveis
                              </h6>
                              <small className="text-muted d-block mt-1">
                                Posição: <strong>{selectedQueuePosition.name}</strong> - {selectedQueuePosition.people} pessoa{selectedQueuePosition.people !== 1 ? 's' : ''}
                              </small>
                            </div>
                            <div className="card-body p-0">
                              {/* Lista de Propostas em Accordion */}
                              <div className="list-group list-group-flush">
                                {queueProposals.map((proposal, index) => {
                                  const isSelected = selectedQueueProposalIndex === index
                                  const totalCapacity = proposal.totalCapacity || 0
                                  const wastePercentage = proposal.wastePercentage || 0
                                  
                                  // Determinar badge baseado na eficiência
                                  let badge = '✓ Bom'
                                  let badgeClass = 'bg-success'
                                  
                                  if (proposal.badge === 'Ideal') {
                                    badge = '⭐ Ideal'
                                    badgeClass = 'bg-success'
                                  } else if (proposal.badge === 'Muito Bom') {
                                    badge = '👍 Muito Bom'
                                    badgeClass = 'bg-success'
                                  } else if (proposal.badge === 'Bom') {
                                    badge = '✓ Bom'
                                    badgeClass = 'bg-info'
                                  } else if (proposal.badge === 'Acceptable') {
                                    badge = '○ Aceitável'
                                    badgeClass = 'bg-warning text-dark'
                                  }

                                  return (
                                    <div
                                      key={proposal.id || index}
                                      className={`list-group-item cursor-pointer p-3 ${
                                        isSelected ? 'bg-success bg-opacity-10 border-success border-2' : 'border-1'
                                      }`}
                                      onClick={() => setSelectedQueueProposalIndex(index)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <div className="d-flex align-items-start gap-3">
                                        <div className="form-check pt-1">
                                          <input
                                            className="form-check-input"
                                            type="radio"
                                            checked={isSelected}
                                            onChange={() => {}}
                                            style={{ cursor: 'pointer' }}
                                          />
                                        </div>
                                        <div className="flex-grow-1">
                                          <div className="d-flex align-items-center gap-2 mb-2">
                                            <strong className="text-dark">
                                              Opção {index + 1}: {proposal.tableNumbers?.length || 1} mesa{(proposal.tableNumbers?.length || 1) !== 1 ? 's' : ''}
                                            </strong>
                                            <span className={`badge ${badgeClass}`}>
                                              {badge}
                                            </span>
                                            {index === 0 && (
                                              <span className="badge bg-primary">
                                                <i className="bi bi-star-fill me-1"></i>
                                                Recomendado
                                              </span>
                                            )}
                                          </div>

                                          {/* Detalhes da Proposta */}
                                          <div className="row g-2 mb-2 small text-muted">
                                            <div className="col-auto">
                                              <i className="bi bi-diagram-3 me-1"></i>
                                              <strong>Mesas:</strong> {proposal.tableNumbers?.join(' + ') || 'N/A'}
                                            </div>
                                            <div className="col-auto">
                                              <i className="bi bi-chair me-1"></i>
                                              <strong>Capacidade:</strong> {totalCapacity}
                                            </div>
                                            <div className="col-auto">
                                              <i className="bi bi-percent me-1"></i>
                                              <strong>Desperdício:</strong> {wastePercentage.toFixed(1)}%
                                            </div>
                                          </div>

                                          {/* Visualização das Mesas */}
                                          {proposal.tableNumbers && proposal.tableNumbers.length > 0 && (
                                            <div className="d-flex gap-1 flex-wrap">
                                              {proposal.tableNumbers.map((tableNum: string, tIdx: number) => (
                                                <span key={tIdx} className="badge bg-light text-dark border border-success">
                                                  <i className="bi bi-diagram-3 me-1"></i>
                                                  Mesa {tableNum}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          </div>

                          {/* Info da Seleção */}
                          {selectedQueueProposalIndex !== null && queueProposals[selectedQueueProposalIndex] && (
                            <div className="alert alert-info mb-3 py-2 small">
                              <i className="bi bi-info-circle me-2"></i>
                              Selecionada: {queueProposals[selectedQueueProposalIndex].tableNumbers?.length || 1} mesa{(queueProposals[selectedQueueProposalIndex].tableNumbers?.length || 1) !== 1 ? 's' : ''} 
                              {' • '}
                              {queueProposals[selectedQueueProposalIndex].totalCapacity} lugares 
                              {' • '}
                              Desperdício: {(queueProposals[selectedQueueProposalIndex].wastePercentage || 0).toFixed(1)}%
                            </div>
                          )}

                          {/* Botões de Ação */}
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-info flex-grow-1"
                              onClick={() => {
                                setShowQueueProposalSelection(false)
                                setQueueProposals([])
                                setSelectedQueueProposalIndex(null)
                              }}
                              disabled={loading}
                            >
                              <i className="bi bi-x-circle me-2"></i>
                              Voltar
                            </button>
                            <button
                              className="btn btn-success flex-grow-1"
                              onClick={() => {
                                if (selectedQueueProposalIndex !== null && queueProposals[selectedQueueProposalIndex]) {
                                  confirmQueueProposal(queueProposals[selectedQueueProposalIndex], selectedQueuePosition)
                                }
                              }}
                              disabled={loading || selectedQueueProposalIndex === null}
                            >
                              {loading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Confirmando...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-check-circle me-2"></i>
                                  Confirmar Seleção
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {allocation ? (
          <div className="d-flex gap-2 w-100 justify-content-end align-items-center">
            {allocation.status !== 'occupied' && (
              <button
                type="button"
                className="btn btn-sm"
                style={{backgroundColor: '#fd397a', borderColor: '#fd397a', color: 'white'}}
                onClick={async () => {
                  if (showTabsUI && relatedAllocations.length > 1) {
                    setPendingAction({ type: 'occupied', scope: 'multi' })
                    setShowMultiActionDialog(true)
                  } else {
                    const result = await Swal.fire({
                      title: 'Marcar como Ocupada?',
                      text: 'O cliente chegou e está sentado na mesa.',
                      icon: 'question',
                      showCancelButton: true,
                      confirmButtonText: 'Sim, Cliente Chegou',
                      cancelButtonText: 'Cancelar',
                      confirmButtonColor: '#fd397a'
                    })
                    if (result.isConfirmed) {
                      await handleChangeStatusMulti('occupied')
                    }
                  }
                }}
                disabled={loading}
              >
                <i className="bi bi-person-check-fill me-1"></i>
                Marcar Ocupada
                {showTabsUI && relatedAllocations.length > 1 && (
                  <span className="badge bg-light text-dark ms-1" style={{fontSize: '0.7rem'}}>
                    {relatedAllocations.length} mesas
                  </span>
                )}
              </button>
            )}
            
            {allocation.status !== 'cleaning' && (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={async () => {
                  if (showTabsUI && relatedAllocations.length > 1) {
                    setPendingAction({ type: 'cleaning', scope: 'multi' })
                    setShowMultiActionDialog(true)
                  } else {
                    const result = await Swal.fire({
                      title: 'Iniciar Limpeza?',
                      text: 'A mesa será marcada como "Em Limpeza".',
                      icon: 'question',
                      showCancelButton: true,
                      confirmButtonText: 'Sim, Iniciar Limpeza',
                      cancelButtonText: 'Cancelar',
                      confirmButtonColor: '#0d6efd'
                    })
                    if (result.isConfirmed) {
                      await handleChangeStatusMulti('cleaning')
                    }
                  }
                }}
                disabled={loading}
              >
                <i className="bi bi-trash-fill me-1"></i>
                Iniciar Limpeza
                {showTabsUI && relatedAllocations.length > 1 && (
                  <span className="badge bg-light text-dark ms-1" style={{fontSize: '0.7rem'}}>
                    {relatedAllocations.length} mesas
                  </span>
                )}
              </button>
            )}
            
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => {
                if (showTabsUI && relatedAllocations.length > 1) {
                  setPendingAction({ type: 'deallocate', scope: 'multi' })
                  setShowMultiActionDialog(true)
                } else {
                  Swal.fire({
                    title: 'Desalocar Mesa?',
                    text: 'A mesa será liberada para disponibilidade.',
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Sim, Desalocar',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#dc3545'
                  }).then(result => {
                    if (result.isConfirmed) {
                      handleDeallocateMulti()
                    }
                  })
                }
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  Desalocando...
                </>
              ) : (
                <>
                  <i className="bi bi-unlock me-1"></i>
                  Desalocar Mesa
                  {showTabsUI && relatedAllocations.length > 1 && (
                    <span className="badge bg-light text-dark ms-1" style={{fontSize: '0.7rem'}}>
                      {relatedAllocations.length} mesas
                    </span>
                  )}
                </>
              )}
            </button>
            
            <button
              type="button"
              className="btn btn-dark btn-sm"
              onClick={handleClose}
              disabled={loading}
            >
              <i className="bi bi-x-circle me-1"></i>
              Cancelar
            </button>
          </div>
        ) : (
          <div className="d-flex gap-2 w-100 justify-content-end align-items-center">
            <button
              type="button"
              className="btn btn-dark btn-sm"
              onClick={handleClose}
              disabled={loading}
            >
              <i className="bi bi-x-circle me-1"></i>
              Cancelar
            </button>
          </div>
        )}
      </Modal.Footer>

      {/* Dialog de Confirmação: Qual Ação Executar com Múltiplas Mesas */}
      <Modal show={showMultiActionDialog} onHide={() => setShowMultiActionDialog(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-diagram-3 me-2 text-info"></i>
            Como Executar a Ação?
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!allocation ? (
            <div className="alert alert-danger" role="alert">
              <i className="bi bi-exclamation-circle me-2"></i>
              <strong>Erro:</strong> Dados da alocação não encontrados. Por favor, feche e selecione uma mesa novamente.
            </div>
          ) : (
            <>
              <div className="alert alert-info mb-3 py-2">
                <i className="bi bi-info-circle-fill me-2"></i>
                <strong>Informação:</strong> Esta reserva utiliza <strong>{relatedAllocations.length} mesa(s)</strong> no conjunto.
              </div>

              <p className="fw-bold mb-3">Selecione o escopo da ação:</p>

              <div className="d-flex flex-column gap-3">
                {/* Option 1: Apenas esta mesa (DEFAULT/RECOMENDADO) */}
                <div
                  className="card border-2 p-3"
                  style={{
                    cursor: 'pointer',
                    borderColor: selectedAllocationIds.size === 1 && selectedAllocationIds.has(allocation.id) ? '#0d6efd' : '#dee2e6',
                    backgroundColor: selectedAllocationIds.size === 1 && selectedAllocationIds.has(allocation.id) ? '#e7f1ff' : '#f8f9fa'
                  }}
                  onClick={() => {
                    setSelectedAllocationIds(new Set([allocation.id]))
                  }}
                >
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="option-single"
                      name="multiAction"
                      checked={selectedAllocationIds.size === 1 && selectedAllocationIds.has(allocation.id)}
                      onChange={() => setSelectedAllocationIds(new Set([allocation.id]))}
                    />
                    <label className="form-check-label w-100" htmlFor="option-single">
                      <strong>
                        <i className="bi bi-square-fill me-2 text-success"></i>
                        Apenas a Mesa Atual
                      </strong>
                      <br />
                      <small className="text-muted ms-4 d-block">
                        Mesa <strong>{table?.tableNumber}</strong> (apenas esta)
                      </small>
                      <span className="badge bg-success ms-4 mt-2">
                        <i className="bi bi-check-circle me-1"></i>
                        Recomendado
                      </span>
                    </label>
                  </div>
                </div>

                {/* Option 2: Todas as mesas do conjunto */}
                <div
                  className="card border-2 p-3"
                  style={{
                    cursor: 'pointer',
                    borderColor: selectedAllocationIds.size > 1 ? '#0d6efd' : '#dee2e6',
                    backgroundColor: selectedAllocationIds.size > 1 ? '#fff5e6' : '#f8f9fa'
                  }}
                  onClick={() => {
                    const allIds = new Set(relatedAllocations.map(a => a.id))
                    setSelectedAllocationIds(allIds)
                  }}
                >
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="option-all"
                      name="multiAction"
                      checked={selectedAllocationIds.size > 1}
                      onChange={() => {
                        const allIds = new Set(relatedAllocations.map(a => a.id))
                        setSelectedAllocationIds(allIds)
                      }}
                    />
                    <label className="form-check-label w-100" htmlFor="option-all">
                      <strong>
                        <i className="bi bi-diagram-3-fill me-2 text-warning"></i>
                        Todas as Mesas do Conjunto
                      </strong>
                      <br />
                      <small className="text-muted ms-4 d-block">
                        {relatedAllocations.map((a, idx) => {
                          const t = availableTables?.find(table => table.id === a.environmentTableId)
                          return (
                            <span key={a.id}>
                              {idx > 0 && ' + '}
                              <strong>{t?.tableNumber || `Mesa #${a.environmentTableId}`}</strong>
                            </span>
                          )
                        })}
                      </small>
                      <span className="badge bg-warning text-dark ms-4 mt-2">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        {relatedAllocations.length} mesa(s)
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="alert alert-warning mt-4 mb-0 py-2 small">
                <i className="bi bi-lightbulb me-2"></i>
                <strong>Dica:</strong> Escolha "Apenas a Mesa Atual" para ações em uma mesa específica. Escolha "Todas" se a ação se aplica a toda a reserva.
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowMultiActionDialog(false)}
          >
            Cancelar
          </button>

          <button
            type="button"
            className={`btn ${
              pendingAction?.type === 'occupied'
                ? ''
                : pendingAction?.type === 'cleaning'
                ? 'btn-primary'
                : 'btn-danger'
            }`}
            style={
              pendingAction?.type === 'occupied'
                ? {backgroundColor: '#fd397a', borderColor: '#fd397a', color: 'white'}
                : {}
            }
            onClick={async () => {
              setShowMultiActionDialog(false)

              if (!pendingAction) return

              // Garantir que sempre tem pelo menos uma seleção
              if (selectedAllocationIds.size === 0) {
                setSelectedAllocationIds(new Set([allocation!.id]))
              }

              switch (pendingAction.type) {
                case 'occupied':
                  Swal.fire({
                    title: 'Marcar como Ocupada?',
                    text: `O cliente chegou. Confirmar para ${selectedAllocationIds.size > 1 ? `as ${selectedAllocationIds.size} mesas` : 'esta mesa'}?`,
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Sim, Marcar Ocupada',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#fd397a'
                  }).then(result => {
                    if (result.isConfirmed) {
                      handleChangeStatusMulti('occupied')
                      setPendingAction(null)
                    }
                  })
                  break

                case 'cleaning':
                  Swal.fire({
                    title: 'Iniciar Limpeza?',
                    text: `A mesa será marcada como "Em Limpeza". Confirmar para ${selectedAllocationIds.size > 1 ? `as ${selectedAllocationIds.size} mesas` : 'esta mesa'}?`,
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Sim, Iniciar Limpeza',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#0d6efd'
                  }).then(result => {
                    if (result.isConfirmed) {
                      handleChangeStatusMulti('cleaning')
                      setPendingAction(null)
                    }
                  })
                  break

                case 'deallocate':
                  Swal.fire({
                    title: 'Desalocar Mesa(s)?',
                    text: `A(s) mesa(s) será(ão) liberada(s). Confirmar para ${selectedAllocationIds.size > 1 ? `as ${selectedAllocationIds.size} mesas` : 'esta mesa'}?`,
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Sim, Desalocar',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#dc3545'
                  }).then(result => {
                    if (result.isConfirmed) {
                      handleDeallocateMulti()
                      setPendingAction(null)
                    }
                  })
                  break
              }
            }}
            disabled={loading || selectedAllocationIds.size === 0}
          >
            {selectedAllocationIds.size === 0 ? (
              <>
                <i className="bi bi-exclamation-triangle me-1"></i>
                Selecione uma Opção
              </>
            ) : (
              <>
                {pendingAction?.type === 'occupied' && (
                  <>
                    <i className="bi bi-person-check-fill me-1"></i>
                    Confirmar: Marcar Ocupada
                  </>
                )}
                {pendingAction?.type === 'cleaning' && (
                  <>
                    <i className="bi bi-trash-fill me-1"></i>
                    Confirmar: Iniciar Limpeza
                  </>
                )}
                {pendingAction?.type === 'deallocate' && (
                  <>
                    <i className="bi bi-unlock me-1"></i>
                    Confirmar: Desalocar
                  </>
                )}
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </Modal>
  )
}
